// PreviewModal.jsx
import React from "react";
import {
    Modal,
    Box,
    IconButton,
    Typography,
    Paper,
    Button,
    Tooltip,
} from "@mui/material";
import {
    Close as CloseIcon,
    Fullscreen,
    FullscreenExit,
    ZoomIn,
    ZoomOut,
    RotateLeft,
    RotateRight
} from "@mui/icons-material";

const PreviewModal = ({ open, onClose, children, title = "Preview" }) => {
    const [isFullscreen, setIsFullscreen] = React.useState(false);
    const [zoom, setZoom] = React.useState(1);
    const [rotation, setRotation] = React.useState(0);

    const handleFullscreen = () => {
        setIsFullscreen(!isFullscreen);
    };

    const handleZoomIn = () => {
        setZoom(prev => Math.min(prev + 0.1, 1));
    };

    const handleZoomOut = () => {
        setZoom(prev => Math.max(prev - 0.1, 0.2));
    };

    const handleRotateLeft = () => {
        setRotation(prev => prev - 90);
    };

    const handleRotateRight = () => {
        setRotation(prev => prev + 90);
    };

    const handleReset = () => {
        setZoom(1);
        setRotation(0);
    };

    const modalStyle = {
        position: "absolute",
        top: "50%",
        left: "50%",
        transform: "translate(-50%, -50%)",
        width: isFullscreen ? "100vw" : "90vw",
        height: isFullscreen ? "100vh" : "90vh",
        bgcolor: "background.paper",
        boxShadow: 24,
        borderRadius: isFullscreen ? 0 : 2,
        overflow: "hidden",
        display: "flex",
        flexDirection: "column",
        outline: 'none',
    };

    return (
        <Modal
            open={open}
            onClose={onClose}
            aria-labelledby="preview-modal"
            aria-describedby="branding-settings-preview"
            sx={{
                backdropFilter: 'blur(4px)',
            }}
        >
            <Box sx={modalStyle}>
                {/* Header */}
                <Box
                    sx={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                        p: 2,
                        borderBottom: "1px solid",
                        borderColor: "divider",
                        bgcolor: "background.paper",
                        flexShrink: 0,
                    }}
                >
                    <Typography variant="h6" component="h2">
                        {title}
                    </Typography>
                    <Box display="flex" alignItems="center" gap={1}>
                        <Typography variant="body2" color="text.secondary" sx={{ mr: 1 }}>
                            Zoom: {Math.round(zoom * 100)}%
                        </Typography>
                        <Tooltip title="Zoom In">
                            <IconButton onClick={handleZoomIn} size="small">
                                <ZoomIn />
                            </IconButton>
                        </Tooltip>
                        <Tooltip title="Zoom Out">
                            <IconButton onClick={handleZoomOut} size="small">
                                <ZoomOut />
                            </IconButton>
                        </Tooltip>
                        <Tooltip title="Rotate Left">
                            <IconButton onClick={handleRotateLeft} size="small">
                                <RotateLeft />
                            </IconButton>
                        </Tooltip>
                        <Tooltip title="Rotate Right">
                            <IconButton onClick={handleRotateRight} size="small">
                                <RotateRight />
                            </IconButton>
                        </Tooltip>
                        <Tooltip title="Reset View">
                            <Button
                                onClick={handleReset}
                                size="small"
                                variant="outlined"
                                sx={{ ml: 1 }}
                            >
                                Reset
                            </Button>
                        </Tooltip>
                        <Tooltip title={isFullscreen ? "Exit Fullscreen" : "Enter Fullscreen"}>
                            <IconButton onClick={handleFullscreen} sx={{ ml: 1 }}>
                                {isFullscreen ? <FullscreenExit /> : <Fullscreen />}
                            </IconButton>
                        </Tooltip>
                        <Tooltip title="Close Preview">
                            <IconButton onClick={onClose}>
                                <CloseIcon />
                            </IconButton>
                        </Tooltip>
                    </Box>
                </Box>

                {/* Content */}
                <Box
                    sx={{
                        flex: 1,
                        overflow: "auto",
                        bgcolor: "#f5f5f5",
                        position: "relative",
                        display: "flex",
                        justifyContent: "center",
                        alignItems: "flex-start", // important
                        p: 2,
                    }}
                >
                    <Box
                        sx={{
                            width: "100%",
                            height: "1920px",
                            backgroundColor: "#999",
                            transform: `scale(${zoom}) rotate(${rotation}deg)`,
                            transformOrigin: "top center",
                            transition: "transform 0.3s ease",
                            
                        }}
                    >
                        {children}
                    </Box>
                </Box>

                {/* Footer */}
                <Box
                    sx={{
                        p: 2,
                        borderTop: "1px solid",
                        borderColor: "divider",
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                        flexShrink: 0,
                        bgcolor: "background.paper",
                    }}
                >
                    <Typography variant="body2" color="text.secondary">
                        Use zoom controls to adjust the preview size. Device height: 1920px
                    </Typography>
                    <Box display="flex" gap={1}>
                        <Button onClick={handleFullscreen} variant="outlined" size="small">
                            {isFullscreen ? "Exit Fullscreen" : "Fullscreen"}
                        </Button>
                        <Button onClick={onClose} variant="contained" color="primary" size="small">
                            Close Preview
                        </Button>
                    </Box>
                </Box>
            </Box>
        </Modal>
    );
};

export default PreviewModal;